import React, { useState } from 'react';
import { ZoomIn, ZoomOut, RotateCcw, ChevronLeft, ChevronRight, Calendar, ShoppingCart, MessageCircle, AlertTriangle, FileText } from 'lucide-react';
import { toast } from 'sonner';

const PlayerHealth = () => {
  const [selectedInjury, setSelectedInjury] = useState('right-knee');
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);

  // Player info
  const player = {
    name: 'Jayson Tatum',
    number: '#0',
    position: 'Forward'
  };

  // Diagnostic results
  const diagnosticResults = [
    { id: 1, date: 'August 27, 2023', title: 'Initial examination', hasWarning: false },
    { id: 2, date: 'August 27, 2023', title: 'MRI: right knee', hasWarning: false },
    { id: 3, date: 'August 29, 2023', title: 'Blood screening: CRP, ESR', hasWarning: true },
  ];

  // Treatment plan
  const treatmentPlan = [
    {
      id: 1,
      time: '12:30 PM - 01:15 PM',
      type: 'Office visit',
      title: 'MRI: right thigh, knee and shin',
      doctor: { name: 'Ralph J. Edwards', role: 'MD, Radiologist', avatar: 'RE' },
      isSoon: true
    },
    {
      id: 2,
      time: '02:30 PM - 03:15 PM',
      type: 'Consultation',
      title: 'Surgery preparation assessment',
      doctor: { name: 'Brooklyn S. Simmons', role: 'MD, Sports Medicine', avatar: 'BS' },
      isSoon: false
    },
    {
      id: 3,
      time: '04:00 PM - 04:45 PM',
      type: 'Consultation',
      title: 'Physical rehabilitation planning',
      doctor: { name: 'Kristin Watson', role: 'MD, Physical Therapist', avatar: 'KW' },
      isSoon: false
    },
  ];

  // Medications
  const medications = [
    { id: 1, name: 'Knee orthosis', description: 'Fixing device', icon: '🦴' },
    { id: 2, name: 'Ibuprofen', description: 'Tablets - 800mg', icon: '💊' },
    { id: 3, name: 'Diclofenac', description: 'Ointment - 2%', icon: '🧴' },
    { id: 4, name: 'Ice Pack', description: 'Cold therapy', icon: '🧊' },
  ];

  // Additional files
  const additionalFiles = [
    { id: 1, name: 'Medical Record.pdf' },
    { id: 2, name: 'X-Ray Results.pdf' },
    { id: 3, name: 'Treatment Protocol.pdf' },
  ];

  // Injury points on body
  const injuryPoints = [
    { id: 'left-shoulder', x: 38, y: 18, color: '#3b82f6', label: 'Left Shoulder' },
    { id: 'right-shoulder', x: 62, y: 18, color: '#3b82f6', label: 'Right Shoulder' },
    { id: 'torso', x: 50, y: 35, color: '#eab308', label: 'Torso' },
    { id: 'right-knee', x: 55, y: 62, color: '#ef4444', label: 'Right Knee', isMain: true },
  ];

  const handleZoom = (direction) => {
    setZoom(prev => direction === 'in' ? Math.min(prev + 0.2, 2) : Math.max(prev - 0.2, 0.6));
  };

  const handleRotate = (direction) => {
    setRotation(prev => direction === 'left' ? prev - 15 : prev + 15);
  };

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white">Player Health</h1>
          <p className="text-gray-400 mt-1">{player.name} {player.number} - Medical Dashboard</p>
        </div>

        <div className="flex gap-6">
          {/* Left Column - Diagnostics & Treatment */}
          <div className="w-96 flex-shrink-0 space-y-6">
            {/* Diagnostic Results */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-semibold text-lg">Diagnostic results</h2>
                <button className="text-gray-400 text-sm hover:text-white">All results (5) &gt;</button>
              </div>
              <div className="space-y-3">
                {diagnosticResults.map(result => (
                  <div 
                    key={result.id}
                    className="bg-gray-800/50 rounded-lg p-4 hover:bg-gray-800 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-gray-400 text-sm">{result.date}</p>
                        <p className="text-white font-medium mt-1">{result.title}</p>
                      </div>
                      {result.hasWarning && (
                        <AlertTriangle size={18} className="text-yellow-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Treatment Plan */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-semibold text-lg">Treatment plan</h2>
                <div className="flex items-center gap-2">
                  <button className="p-1 text-gray-400 hover:text-white">
                    <ChevronLeft size={18} />
                  </button>
                  <button className="p-1 text-gray-400 hover:text-white">
                    <ChevronRight size={18} />
                  </button>
                  <button className="p-1 text-gray-400 hover:text-white">
                    <Calendar size={18} />
                  </button>
                </div>
              </div>

              <p className="text-gray-500 text-sm uppercase mb-4">Today, August 30</p>

              <div className="space-y-4">
                {treatmentPlan.map(appointment => (
                  <div key={appointment.id} className="border-l-2 border-gray-700 pl-4">
                    {appointment.isSoon && (
                      <span className="inline-block px-2 py-1 bg-orange-500 text-white text-xs rounded mb-2">SOON</span>
                    )}
                    <p className="text-gray-400 text-sm">{appointment.time} • {appointment.type}</p>
                    <p className="text-white font-medium mt-1">{appointment.title}</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                          {appointment.doctor.avatar}
                        </div>
                        <div>
                          <p className="text-white text-sm">{appointment.doctor.name}</p>
                          <p className="text-gray-500 text-xs">{appointment.doctor.role}</p>
                        </div>
                      </div>
                      <button className="p-2 text-gray-400 hover:text-white">
                        <MessageCircle size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button className="text-gray-400 text-sm mt-4 hover:text-white">
                Full treatment plan &gt;
              </button>
            </div>
          </div>

          {/* Center - 3D Body Model */}
          <div className="flex-1">
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-6 h-full">
              {/* Zoom Controls */}
              <div className="flex justify-end gap-2 mb-4">
                <button 
                  onClick={() => handleZoom('in')}
                  className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white"
                >
                  <ZoomIn size={18} />
                </button>
                <button 
                  onClick={() => handleZoom('out')}
                  className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white"
                >
                  <ZoomOut size={18} />
                </button>
                <button 
                  onClick={() => { setRotation(0); setZoom(1); }}
                  className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white"
                >
                  <RotateCcw size={18} />
                </button>
              </div>

              {/* Body Model */}
              <div className="relative flex justify-center items-center min-h-[500px]" style={{ transform: `scale(${zoom})` }}>
                {/* SVG Human Body */}
                <svg viewBox="0 0 100 140" className="w-64 h-auto" style={{ transform: `rotateY(${rotation}deg)` }}>
                  {/* Head */}
                  <circle cx="50" cy="12" r="10" fill="#6b7280" />
                  <circle cx="47" cy="10" r="1.5" fill="#374151" />
                  <circle cx="53" cy="10" r="1.5" fill="#374151" />
                  <path d="M 47 14 Q 50 16 53 14" stroke="#374151" strokeWidth="1" fill="none" />
                  
                  {/* Neck */}
                  <rect x="46" y="22" width="8" height="6" fill="#6b7280" rx="2" />
                  
                  {/* Torso */}
                  <path d="M 35 28 L 65 28 L 62 60 L 38 60 Z" fill="#6b7280" />
                  
                  {/* Arms */}
                  <path d="M 35 28 L 25 32 L 20 55 L 25 55 L 32 38 L 35 35" fill="#6b7280" />
                  <path d="M 65 28 L 75 32 L 80 55 L 75 55 L 68 38 L 65 35" fill="#6b7280" />
                  
                  {/* Hands */}
                  <circle cx="20" cy="58" r="4" fill="#6b7280" />
                  <circle cx="80" cy="58" r="4" fill="#6b7280" />
                  
                  {/* Pelvis */}
                  <path d="M 38 60 L 62 60 L 58 72 L 42 72 Z" fill="#6b7280" />
                  
                  {/* Legs */}
                  <path d="M 42 72 L 44 100 L 42 115 L 48 115 L 50 100 L 50 72" fill="#6b7280" />
                  <path d="M 58 72 L 56 100 L 58 115 L 52 115 L 50 100 L 50 72" fill="#6b7280" />
                  
                  {/* Feet */}
                  <ellipse cx="45" cy="118" rx="6" ry="3" fill="#6b7280" />
                  <ellipse cx="55" cy="118" rx="6" ry="3" fill="#6b7280" />
                  
                  {/* Injury Points */}
                  {injuryPoints.map(point => (
                    <g key={point.id}>
                      <circle 
                        cx={point.x} 
                        cy={point.y} 
                        r={point.isMain ? 6 : 4}
                        fill={point.color}
                        className="cursor-pointer"
                        onClick={() => setSelectedInjury(point.id)}
                      />
                      {point.isMain && (
                        <>
                          <circle cx={point.x} cy={point.y} r="8" fill="none" stroke={point.color} strokeWidth="1" opacity="0.5">
                            <animate attributeName="r" from="6" to="12" dur="1.5s" repeatCount="indefinite" />
                            <animate attributeName="opacity" from="0.5" to="0" dur="1.5s" repeatCount="indefinite" />
                          </circle>
                        </>
                      )}
                    </g>
                  ))}
                </svg>

                {/* Injury Popup */}
                {selectedInjury === 'right-knee' && (
                  <div className="absolute right-4 bottom-20 bg-gray-900 border border-yellow-500 rounded-xl p-4 w-48">
                    <div className="flex items-center justify-center mb-3">
                      <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center">
                        <span className="text-3xl">🦵</span>
                      </div>
                    </div>
                    <p className="text-center text-yellow-400 text-sm font-medium">Grade 2 Sprain</p>
                  </div>
                )}
              </div>

              {/* Controls Footer */}
              <div className="text-center text-gray-500 text-sm mt-4">
                <span className="mr-4">🖱 Drag to rotate</span>
                <span className="mr-4">🔍 Zoom controls</span>
                <span>Rotation: {rotation}°</span>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Diagnosis & Medication */}
          <div className="w-80 flex-shrink-0 space-y-4">
            {/* Main Diagnosis */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-5">
              <h2 className="text-white font-semibold text-lg mb-4">Right knee</h2>
              
              <div className="mb-4">
                <p className="text-gray-500 text-xs uppercase mb-1">Main Diagnosis</p>
                <p className="text-green-400">Right knee - Medial collateral ligament sprain [S83.41]</p>
              </div>

              <div>
                <p className="text-gray-500 text-xs uppercase mb-2">Doctor in Charge</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                      BS
                    </div>
                    <div>
                      <p className="text-white font-medium">Dr. Brooklyn S. Simmons</p>
                      <p className="text-gray-500 text-sm">MD, Sports Medicine Specialist</p>
                    </div>
                  </div>
                  <button className="p-2 text-gray-400 hover:text-white">
                    <MessageCircle size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* Medication */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-semibold text-lg">Medication</h2>
                <button className="text-gray-400 text-sm hover:text-white">Buy meds (6) &gt;</button>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {medications.map(med => (
                  <div key={med.id} className="bg-gray-800/50 rounded-lg p-4">
                    <div className="text-3xl mb-2 text-center">{med.icon}</div>
                    <p className="text-white font-medium text-sm">{med.name}</p>
                    <p className="text-gray-500 text-xs mb-3">{med.description}</p>
                    <button 
                      onClick={() => toast.success(`${med.name} added to cart`)}
                      className="w-full flex items-center justify-center gap-2 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm transition-colors"
                    >
                      <ShoppingCart size={14} />
                      Add
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Information */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-5">
              <h2 className="text-white font-semibold text-lg mb-4">Additional information</h2>
              
              <div className="space-y-3">
                {additionalFiles.map(file => (
                  <div 
                    key={file.id}
                    className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <FileText size={18} className="text-red-400" />
                      <span className="text-white">{file.name}</span>
                    </div>
                    <ChevronRight size={16} className="text-gray-400" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerHealth;
